/**
 * Navigation + property-taxonomy hooks
 *
 * Rebuild the legacy `constants.js` shapes (Links / HomeLinks /
 * ParentPropertyTypes / propertySubTypes) from the enums API, so consuming
 * components keep their existing rendering logic with minimal changes.
 *
 *   nav.role_menu         → role-based menus (constants `Links`)
 *   nav.home_links        → logged-out mega-menu / footer (constants `HomeLinks`)
 *   property.search_types → search taxonomy tree (constants `ParentPropertyTypes`)
 *   property.subtypes     → flat subtype list (constants `propertySubTypes`)
 *
 * Menu route lives in each tree node's `flags.path`; HomeLinks subcategory
 * header flag lives in `flags.header`. Env-var "Tasks" links are appended
 * here in code (kept out of the seed so enum data stays env-agnostic).
 */

import { useMemo } from 'react';
import { useEnumTree, useEnumOptions } from './useEnum';

// Roles whose menu includes the env-based "Tasks" link (mirrors constants Links).
const ROLES_WITH_TASKS = new Set(['admin', 'staff', 'builderteam']);

function tasksLink() {
  return { label: 'Due', path: process.env.REACT_APP_TASKS_BASE_URL, target: '_blank' };
}

// "Profile" renders as a parent with these children when the enum tree does not
// supply its own third level.
const DEFAULT_PROFILE_CHILDREN = [
  { label: 'Edit Profile', path: '/profile/edit-profile' },
  { label: 'Notification Settings', path: '/notification-settings' },
  { label: 'Ratings & Reviews', path: '/my-reviews' },
];

const isProfileItem = (node) =>
  /^profile$/i.test(node.label || '') || node.flags?.path === '/profile/edit-profile';

// Buyers are not rated, so they don't get the "Ratings & Reviews" item.
const ROLES_WITHOUT_REVIEWS = new Set(['buyer']);
const isReviewsItem = (item) =>
  item.path === '/my-reviews' || /ratings?\s*(&|and)\s*reviews/i.test(item.label || '');

/**
 * Build the child list for a nav node: third-level enum children when present,
 * otherwise the hard-coded Profile children. Items not applicable to `role`
 * are dropped.
 */
function navChildren(node, role) {
  const enumKids = (node.children || []).map((c) => ({
    label: c.label, path: c.flags?.path, target: c.flags?.target,
  }));
  const kids = enumKids.length
    ? enumKids
    : (isProfileItem(node) ? DEFAULT_PROFILE_CHILDREN : null);
  if (!kids) return undefined;
  return ROLES_WITHOUT_REVIEWS.has(role) ? kids.filter((k) => !isReviewsItem(k)) : kids;
}

// Admin configuration pages grouped under one "Settings" parent. Built here so a
// flat nav.role_menu (seeded before the submenu existed) still renders grouped;
// when the enum already nests a "Settings" parent, that one wins.
const SETTINGS_GROUP_LABEL = 'Settings';
const SETTINGS_CHILDREN = [
  { label: 'Custom Fields', path: '/admin/custom-fields/' },
  { label: 'Services', path: '/admin/services/' },
  { label: 'Enum Manager', path: '/admin/enums/' },
  { label: 'Translations', path: '/admin/translations/' },
  { label: 'System Settings', path: '/admin/settings-admin/' },
  { label: 'User Rules', path: '/admin/user-rules/' },
];
const SETTINGS_PATHS = new Set(SETTINGS_CHILDREN.map((c) => c.path));

/**
 * Fold top-level settings pages into a "Settings" parent, placed where the
 * first of them was. Only the pages the menu already offers are included, so a
 * role never gains links it didn't have; a lone match stays a plain item.
 */
function groupSettings(items) {
  const alreadyNested = items.some(
    (i) => i.children && i.label?.trim().toLowerCase() === SETTINGS_GROUP_LABEL.toLowerCase(),
  );
  if (alreadyNested) return items;
  const present = new Set(items.filter((i) => !i.children && SETTINGS_PATHS.has(i.path)).map((i) => i.path));
  if (present.size < 2) return items;

  const children = SETTINGS_CHILDREN.filter((c) => present.has(c.path));
  const firstIndex = items.findIndex((i) => !i.children && present.has(i.path));
  const rest = items.filter((i) => i.children || !present.has(i.path));
  rest.splice(firstIndex, 0, { label: SETTINGS_GROUP_LABEL, children });
  return rest;
}

/**
 * Drop top-level items that are now nested under a parent, so an item the enum
 * still lists as a sibling (e.g. "Notification Settings") is not shown twice.
 */
function dropNestedDuplicates(items) {
  const nested = new Set();
  items.forEach((item) => (item.children || []).forEach((child) => {
    if (child.path) nested.add(child.path);
    if (child.label) nested.add(child.label.trim().toLowerCase());
  }));
  if (!nested.size) return items;
  return items.filter((item) => item.children
    || !(nested.has(item.path) || nested.has((item.label || '').trim().toLowerCase())));
}

// ─── useRoleLinksMap ───────────────────────────────────────────────────────────

/**
 * Rebuild the `Links` object — { role: [{ label, path }] } — from nav.role_menu.
 * Env-based Tasks links are appended for the relevant roles.
 */
export function useRoleLinksMap() {
  const { tree, loading } = useEnumTree('nav.role_menu');

  const links = useMemo(() => {
    const map = {};
    for (const roleNode of tree) {
      const items = (roleNode.children || []).map((c) => {
        const children = navChildren(c, roleNode.value);
        return {
          label: c.label,
          // A parent item has no destination of its own — it expands its children.
          path: children ? undefined : c.flags?.path,
          ...(children || !c.flags?.target ? {} : { target: c.flags.target }),
          ...(children ? { children } : {}),
        };
      });
      if (ROLES_WITH_TASKS.has(roleNode.value)) items.push(tasksLink());
      map[roleNode.value] = groupSettings(dropNestedDuplicates(items));
    }
    return map;
  }, [tree]);

  return { links, loading };
}

/**
 * Resolve a role array to a deduped list of nav items.
 * Mirrors the old getUserLinks(): unknown roles fall back to the 'agent' menu.
 *
 * @param {string[]} roles
 * @returns {{ items: Array<{label, path}>, loading: boolean }}
 */
export function useRoleLinks(roles) {
  const { links, loading } = useRoleLinksMap();

  const items = useMemo(() => {
    if (!roles || !roles.length) return [];
    const seen = new Set();
    (roles || []).forEach((role) => {
      const list = links[role] || links['agent'] || [];
      list.forEach((link) => seen.add(JSON.stringify(link)));
    });
    // Filter again after merging roles: a sibling duplicate can come from one
    // role while the nesting parent comes from another.
    // Group again: merged roles (e.g. superadmin) can each contribute settings pages.
    const list = groupSettings(dropNestedDuplicates(Array.from(seen).map((s) => JSON.parse(s))));
    // Pin the "Service Requests" item first (path /search/), keeping the rest in order.
    const idx = list.findIndex(
      (i) => i.path === '/search/' || /to-?do|service request/i.test(i.label || ''),
    );
    if (idx > 0) {
      const [todo] = list.splice(idx, 1);
      list.unshift(todo);
    }
    return list;
  }, [roles, links]);

  return { items, loading };
}

// ─── useHomeLinks ────────────────────────────────────────────────────────────

/**
 * Rebuild the `HomeLinks` array:
 *   [{ label, path, subcategories: [{ label, path, header }] }]
 *
 * @returns {{ homeLinks: Array, loading: boolean }}
 */
export function useHomeLinks() {
  const { tree, loading } = useEnumTree('nav.home_links');

  const homeLinks = useMemo(() => tree.map((cat) => ({
    label: cat.label,
    path: cat.flags?.path,
    subcategories: (cat.children || []).map((sub) => ({
      label: sub.label,
      path: sub.flags?.path,
      header: sub.flags?.header === true,
    })),
  })), [tree]);

  return { homeLinks, loading };
}

// ─── usePropertyTypes ───────────────────────────────────────────────────────────

/**
 * Rebuild the search taxonomy shapes:
 *   ParentPropertyTypes → { key: { label, value, childs: [{ label, value }] } }
 *   propertySubTypes    → [{ label, value }]
 *
 * @returns {{ ParentPropertyTypes: Object, propertySubTypes: Array, loading: boolean }}
 */
export function usePropertyTypes() {
  const { tree, loading: treeLoading } = useEnumTree('property.search_types');
  const { options, loading: optLoading } = useEnumOptions('property.subtypes');

  const ParentPropertyTypes = useMemo(() => {
    const map = {};
    for (const parent of tree) {
      map[parent.value] = {
        label: parent.label,
        value: parent.value,
        // child `value` is prefixed in the enum for uniqueness; real value is in flags.value
        childs: (parent.children || []).map((c) => ({ label: c.label, value: c.flags?.value ?? c.value })),
      };
    }
    return map;
  }, [tree]);

  const propertySubTypes = useMemo(
    () => options.map((o) => ({ label: o.label, value: o.value })),
    [options],
  );

  return { ParentPropertyTypes, propertySubTypes, loading: treeLoading || optLoading };
}
